import { formatTime, parseDateValue } from "../date/month";

const AVAILABILITY_MODES = Object.freeze({
  AVAILABILITY: "availability",
  ABSENCE: "absence",
});

const DAY_START_MINUTES = 0;
const DAY_END_MINUTES = 24 * 60 - 1;
const WEEKDAY_LABELS = Object.freeze(["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"]);

function toDayStart(value) {
  const parsed = parseDateValue(value);
  if (!parsed) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function addDays(date, amount) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
}

function getTimeValue(date) {
  return date instanceof Date ? date.getTime() : Number.NaN;
}

function maxDate(left, right) {
  return getTimeValue(left) >= getTimeValue(right) ? left : right;
}

function minDate(left, right) {
  return getTimeValue(left) <= getTimeValue(right) ? left : right;
}

function overlapsDay(record, dayKey) {
  const dayStart = toDayStart(dayKey);
  const start = parseDateValue(record?.start);
  const end = parseDateValue(record?.end);
  if (!dayStart || !start || !end) return false;
  const nextDayStart = addDays(dayStart, 1);
  return start < nextDayStart && end >= dayStart;
}

function overlapsRange(start, end, rangeStart, rangeEnd) {
  if (!start || !end || !rangeStart || !rangeEnd) return false;
  return start < rangeEnd && end > rangeStart;
}

function minutesFromTime(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function normalizeClockLabel(value) {
  const match = String(value || "").match(/^(\d{1,2}:\d{2})/);
  return match ? match[1] : "";
}

function isFullDayTimeRange(startTime, endTime) {
  const startMinutes = minutesFromTime(startTime);
  const endMinutes = minutesFromTime(endTime);
  if (startMinutes == null || endMinutes == null) return false;
  return startMinutes <= DAY_START_MINUTES && endMinutes >= DAY_END_MINUTES;
}

function isFullDayRecord(record) {
  if (record?.isFullDay) return true;
  return isFullDayTimeRange(formatTime(record?.start), formatTime(record?.end));
}

function formatRangeLabel(startTime, endTime) {
  if (!startTime && !endTime) return "Ganztag";
  if (!startTime) return `bis ${endTime}`;
  if (!endTime) return `ab ${startTime}`;
  return `${startTime} - ${endTime}`;
}

function formatDayLabel(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  const weekday = WEEKDAY_LABELS[date.getDay()] || "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${weekday} ${day}.${month}.`;
}

function getExplicitTimeLabel(record) {
  if (isFullDayRecord(record)) return "Ganztag";
  return formatRangeLabel(formatTime(record?.start), formatTime(record?.end));
}

function getDefaultTimeLabel(record) {
  if (record?.isFullDay || isFullDayTimeRange(record?.startTime, record?.endTime)) return "Ganztag";
  return formatRangeLabel(normalizeClockLabel(record?.startTime), normalizeClockLabel(record?.endTime));
}

function combineDateAndTime(day, timeValue, fallbackTime) {
  const dayStart = toDayStart(day);
  if (!dayStart) return null;

  const timeText = normalizeClockLabel(timeValue) || fallbackTime;
  const minutes = minutesFromTime(timeText);
  if (minutes == null) return null;

  return new Date(dayStart.getFullYear(), dayStart.getMonth(), dayStart.getDate(), Math.floor(minutes / 60), minutes % 60);
}

function getExplicitRangeInterval(record) {
  const start = parseDateValue(record?.start);
  const end = parseDateValue(record?.end);
  if (!start || !end) return null;

  if (!record?.isFullDay) {
    return end > start ? { start, end } : null;
  }

  const startDay = toDayStart(start);
  const endDay = toDayStart(end) || startDay;
  if (!startDay || !endDay) return null;

  return {
    start: startDay,
    end: addDays(endDay, 1),
  };
}

function getDefaultRangeInterval(record, dayDate) {
  const dayStart = toDayStart(dayDate);
  if (!dayStart) return null;

  if (record?.isFullDay || isFullDayTimeRange(record?.startTime, record?.endTime)) {
    return {
      start: dayStart,
      end: addDays(dayStart, 1),
    };
  }

  const start = combineDateAndTime(dayStart, record?.startTime, "00:00");
  let end = combineDateAndTime(dayStart, record?.endTime, "23:59");
  if (!start || !end) return null;
  if (end <= start) end = addDays(end, 1);

  return { start, end };
}

function statusPriority(status) {
  if (status === "unavailable") return 3;
  if (status === "partial") return 2;
  if (status === "available") return 1;
  return 0;
}

function normalizeAvailabilityMode(value, fallback = AVAILABILITY_MODES.ABSENCE) {
  return Object.values(AVAILABILITY_MODES).includes(value) ? value : fallback;
}

function getRelevantRecordType(mode) {
  return mode === AVAILABILITY_MODES.AVAILABILITY ? "availability" : "absence";
}

function isVisibleExplicitRecord(record) {
  return String(record?.releaseStatus ?? record?.release_status ?? "").toLowerCase() !== "draft";
}

function getUserAvailabilityMode(userId, userAvailabilityModes, companyAvailabilityMode) {
  return normalizeAvailabilityMode(userAvailabilityModes?.[userId], normalizeAvailabilityMode(companyAvailabilityMode));
}

function getBaselineSummary(mode) {
  const isAvailabilityMode = mode === AVAILABILITY_MODES.AVAILABILITY;
  return {
    status: isAvailabilityMode ? "unavailable" : "available",
    kind: isAvailabilityMode ? "availability" : "absence",
    label: "",
    tooltip: isAvailabilityMode ? "Keine Verfügbarkeit" : "Keine Absenz",
    mode,
  };
}

function buildFullDaySummary(mode, recordType) {
  const isAvailability = recordType === "availability";
  return {
    status: isAvailability ? "available" : "unavailable",
    kind: recordType,
    label: "",
    tooltip: isAvailability ? "Ganztägig verfügbar" : "Ganztägig abwesend",
    mode,
  };
}

function getRecordDetailText(record) {
  return record?.detailLabel || record?.label || "Ganztag";
}

function getRecordDetailPrefix(record, fallbackPrefix) {
  return Object.prototype.hasOwnProperty.call(record || {}, "detailPrefix") ? record.detailPrefix : fallbackPrefix;
}

function areConsecutiveDays(left, right) {
  if (!(left instanceof Date) || !(right instanceof Date)) return false;
  return addDays(left, 1).getTime() === right.getTime();
}

function buildRecordDetailGroups(records, fallbackPrefix) {
  return records
    .filter((record) => getRecordDetailText(record))
    .map((record) => ({
      startDay: record.dayStart || null,
      endDay: record.dayStart || null,
      label: getRecordDetailText(record),
      prefix: getRecordDetailPrefix(record, fallbackPrefix),
    }))
    .sort((left, right) => {
      const dayCompare = getTimeValue(left.startDay) - getTimeValue(right.startDay);
      if (dayCompare !== 0) return dayCompare;
      const prefixCompare = left.prefix.localeCompare(right.prefix, "de");
      if (prefixCompare !== 0) return prefixCompare;
      return left.label.localeCompare(right.label, "de");
    })
    .reduce((groups, item) => {
      const previousGroup = groups[groups.length - 1];
      if (
        previousGroup &&
        previousGroup.label === item.label &&
        previousGroup.prefix === item.prefix &&
        previousGroup.endDay &&
        item.startDay &&
        areConsecutiveDays(previousGroup.endDay, item.startDay)
      ) {
        previousGroup.endDay = item.endDay;
        return groups;
      }

      groups.push({ ...item });
      return groups;
    }, []);
}

function buildRecordTooltipLabels(records, prefix) {
  const dayKeys = new Set(records.map((record) => record.dayKey).filter(Boolean));

  if (dayKeys.size <= 1) {
    return records
      .map((record) => {
        const label = getRecordDetailText(record);
        const detailPrefix = getRecordDetailPrefix(record, prefix);
        if (!label) return "";
        return detailPrefix ? `${detailPrefix}: ${label}` : label;
      })
      .filter(Boolean);
  }

  return buildRecordDetailGroups(records, prefix).map((group) => {
    const dayLabel =
      group.startDay && group.endDay && group.startDay.getTime() !== group.endDay.getTime()
        ? `${formatDayLabel(group.startDay)} - ${formatDayLabel(group.endDay)}`
        : formatDayLabel(group.startDay);
    return `${dayLabel}: ${group.prefix ? `${group.prefix} ` : ""}${group.label}`;
  });
}

function buildPartialSummary(mode, recordType, records, detailRecords = records) {
  const prefix = recordType === "availability" ? "Verfügbar" : "Abwesend";
  const tooltipLabels = buildRecordTooltipLabels(detailRecords, prefix);

  return {
    status: "partial",
    kind: recordType,
    label: records.find((record) => !record.isFullDay)?.label || "",
    tooltip: tooltipLabels.length ? tooltipLabels.join(", ") : prefix,
    mode,
  };
}

function buildCoveredRangeSummary(mode, recordType, records, detailRecords = records) {
  const isAvailability = recordType === "availability";
  const fallbackTooltip = isAvailability ? "Ganztägig verfügbar" : "Ganztägig abwesend";
  const prefix = isAvailability ? "Verfügbar" : "Abwesend";
  const tooltipLabels = buildRecordTooltipLabels(detailRecords, prefix);

  return {
    status: isAvailability ? "available" : "unavailable",
    kind: recordType,
    label: "",
    tooltip: tooltipLabels.length ? tooltipLabels.join(", ") : fallbackTooltip,
    mode,
  };
}

function summarizeRecords(records, mode) {
  const recordType = getRelevantRecordType(mode);
  const relevantRecords = records.filter((record) => record.type === recordType);
  if (relevantRecords.length) {
    if (relevantRecords.some((record) => record.isFullDay)) {
      return buildFullDaySummary(mode, recordType);
    }
    return buildPartialSummary(mode, recordType, relevantRecords);
  }

  // Zeitfenster-Arbeitstag im Absenz-Modus (siehe selectEffectiveRecords):
  // als Teil-Verfügbarkeit anzeigen, mit den erfassten Zeiten als Label.
  const timedWorkingDayRecords = records.filter(
    (record) => record.type === "availability" && !record.isFullDay
  );
  if (timedWorkingDayRecords.length) {
    return buildPartialSummary(mode, "availability", timedWorkingDayRecords);
  }

  return getBaselineSummary(mode);
}

function clipsCoverRange(records, rangeStart, rangeEnd) {
  const clips = records
    .map((record) => ({
      start: maxDate(record.intervalStart, rangeStart),
      end: minDate(record.intervalEnd, rangeEnd),
    }))
    .filter((clip) => clip.start < clip.end)
    .sort((left, right) => left.start - right.start);

  let coveredUntil = rangeStart;

  for (const clip of clips) {
    if (clip.start > coveredUntil) return false;
    if (clip.end > coveredUntil) coveredUntil = clip.end;
    if (coveredUntil >= rangeEnd) return true;
  }

  return coveredUntil >= rangeEnd;
}

function summarizeRangeRecords(records, mode, rangeStart, rangeEnd, detailRecords = records) {
  const recordType = getRelevantRecordType(mode);
  const relevantRecords = records.filter((record) => record.type === recordType);
  if (!relevantRecords.length) {
    // Nur Zeitfenster-Arbeitstage im Zeitraum (Absenz-Modus): Teil-Verfügbarkeit.
    const timedWorkingDayRecords = records.filter(
      (record) => record.type === "availability" && !record.isFullDay
    );
    if (timedWorkingDayRecords.length) {
      return buildPartialSummary(mode, "availability", timedWorkingDayRecords, detailRecords);
    }
    return getBaselineSummary(mode);
  }

  if (clipsCoverRange(relevantRecords, rangeStart, rangeEnd)) {
    return buildCoveredRangeSummary(mode, recordType, relevantRecords, detailRecords);
  }

  return buildPartialSummary(mode, recordType, relevantRecords, detailRecords);
}

function normalizeExplicitRecord(record, dayKey) {
  if (!["availability", "absence"].includes(record?.type)) return null;
  if (!isVisibleExplicitRecord(record)) return null;
  if (!overlapsDay(record, dayKey)) return null;
  return {
    type: record.type,
    isFullDay: isFullDayRecord(record),
    label: getExplicitTimeLabel(record),
    comment: record.comment || "",
  };
}

function normalizeExplicitRangeRecord(record, dayDate, rangeStart, rangeEnd) {
  if (!["availability", "absence"].includes(record?.type)) return null;
  if (!isVisibleExplicitRecord(record)) return null;

  const interval = getExplicitRangeInterval(record);
  if (!interval || !overlapsRange(interval.start, interval.end, rangeStart, rangeEnd)) return null;
  const dayStart = toDayStart(dayDate);

  return {
    type: record.type,
    intervalStart: interval.start,
    intervalEnd: interval.end,
    isFullDay: isFullDayRecord(record),
    label: getExplicitTimeLabel(record),
    // Praefix nach eigenem Typ, nicht nach Modus: seit Zeitfenster-Arbeitstage
    // im Absenz-Modus mitlaufen, koennen beide Typen im selben Tooltip stehen.
    detailPrefix: record.type === "availability" ? "Verfügbar" : "Abwesend",
    comment: record.comment || "",
    dayKey: formatDateKey(dayStart),
    dayStart,
  };
}

function normalizeDefaultRecord(record, day) {
  if (!["availability", "absence"].includes(record?.type)) return null;
  const timetableWeekday = (Number(day.date?.getDay?.()) + 6) % 7;
  if (Number(record?.weekday) !== timetableWeekday) return null;
  return {
    type: record.type,
    isFullDay: Boolean(record.isFullDay) || isFullDayTimeRange(record.startTime, record.endTime),
    label: getDefaultTimeLabel(record),
    comment: record.comment || "",
  };
}

function normalizeDefaultRangeRecord(record, dayDate, rangeStart, rangeEnd) {
  if (!["availability", "absence"].includes(record?.type)) return null;
  const dayStart = toDayStart(dayDate);
  const timetableWeekday = (Number(dayStart?.getDay?.()) + 6) % 7;
  if (Number(record?.weekday) !== timetableWeekday) return null;

  const interval = getDefaultRangeInterval(record, dayStart);
  if (!interval || !overlapsRange(interval.start, interval.end, rangeStart, rangeEnd)) return null;

  return {
    type: record.type,
    intervalStart: interval.start,
    intervalEnd: interval.end,
    isFullDay: Boolean(record.isFullDay) || isFullDayTimeRange(record.startTime, record.endTime),
    label: getDefaultTimeLabel(record),
    // Praefix nach eigenem Typ, nicht nach Modus — siehe normalizeExplicitRangeRecord.
    detailPrefix: record.type === "availability" ? "Verfügbar" : "Abwesend",
    comment: record.comment || "",
    dayKey: formatDateKey(dayStart),
    dayStart,
  };
}

function selectEffectiveRecords(explicitRecords, defaultRecords, relevantType) {
  const sourceRecords = explicitRecords.length ? explicitRecords : defaultRecords;
  const relevantRecords = sourceRecords.filter((record) => record.type === relevantType);
  if (relevantRecords.length || explicitRecords.length) return relevantRecords;

  // Absenz-Modus: Ein Arbeitstag mit Zeitfenster ("verfügbar 08:00-17:00") hat
  // keinen Absenz-Datensatz und fiele hier durch — die Grundannahme "ganztags
  // verfügbar" schränkt er aber trotzdem ein. Ohne diesen Zweig zeigte der
  // Plan den Tag ganztags grün, die erfassten Zeiten verschwänden.
  return defaultRecords.filter((record) => record.type === "availability" && !record.isFullDay);
}

function getDaysInRange(rangeStart, rangeEnd) {
  const days = [];
  const startDay = toDayStart(rangeStart);
  const endDay = toDayStart(rangeEnd);
  if (!startDay || !endDay) return days;

  for (let cursor = startDay; cursor <= endDay; cursor = addDays(cursor, 1)) {
    days.push(cursor);
  }

  return days;
}

function formatDateKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function buildBaselineDetailRecord(mode, dayStart) {
  const baseline = getBaselineSummary(mode);

  return {
    type: baseline.kind,
    isFullDay: true,
    label: baseline.tooltip,
    detailLabel: baseline.tooltip,
    detailPrefix: "",
    dayKey: formatDateKey(dayStart),
    dayStart,
  };
}

function getRangeRecordEvaluation({ explicitEntries, defaultEntries, relevantType, mode, rangeStart, rangeEnd }) {
  return getDaysInRange(rangeStart, rangeEnd).reduce((evaluation, dayDate) => {
    const dayStart = toDayStart(dayDate);
    if (!dayStart) return evaluation;

    const dayEnd = addDays(dayStart, 1);
    const dayRangeStart = maxDate(dayStart, rangeStart);
    const dayRangeEnd = minDate(dayEnd, rangeEnd);
    if (dayRangeEnd <= dayRangeStart) return evaluation;

    const explicitRecords = explicitEntries
      .map((record) => normalizeExplicitRangeRecord(record, dayStart, dayRangeStart, dayRangeEnd))
      .filter(Boolean);
    const defaultRecords = defaultEntries
      .map((record) => normalizeDefaultRangeRecord(record, dayStart, dayRangeStart, dayRangeEnd))
      .filter(Boolean);

    const effectiveRecords = selectEffectiveRecords(explicitRecords, defaultRecords, relevantType);
    evaluation.records.push(...effectiveRecords);
    evaluation.detailRecords.push(...(effectiveRecords.length ? effectiveRecords : [buildBaselineDetailRecord(mode, dayStart)]));
    return evaluation;
  }, { records: [], detailRecords: [] });
}

export function getAvailabilitySummaryForRange({
  employeeId,
  start,
  end,
  availabilityEntries,
  availabilityDefaults,
  companyAvailabilityMode = AVAILABILITY_MODES.ABSENCE,
  userAvailabilityModes = {},
}) {
  const userId = String(employeeId || "");
  const rangeStart = parseDateValue(start);
  const rangeEnd = parseDateValue(end);
  const mode = getUserAvailabilityMode(userId, userAvailabilityModes, companyAvailabilityMode);

  if (!userId || !rangeStart || !rangeEnd || rangeEnd <= rangeStart) {
    return getBaselineSummary(mode);
  }

  const relevantType = getRelevantRecordType(mode);
  const explicitEntries = (Array.isArray(availabilityEntries) ? availabilityEntries : []).filter(
    (record) => String(record?.userId || "") === userId
  );
  const userDefaults = (Array.isArray(availabilityDefaults) ? availabilityDefaults : []).filter(
    (record) => String(record?.userId || "") === userId
  );
  const rangeEvaluation = getRangeRecordEvaluation({
    explicitEntries,
    defaultEntries: userDefaults,
    relevantType,
    mode,
    rangeStart,
    rangeEnd,
  });

  return summarizeRangeRecords(rangeEvaluation.records, mode, rangeStart, rangeEnd, rangeEvaluation.detailRecords);
}

export function buildAvailabilityByCell({
  availabilityEntries,
  availabilityDefaults,
  companyAvailabilityMode = AVAILABILITY_MODES.ABSENCE,
  userAvailabilityModes = {},
  employees,
  days,
}) {
  const explicitByUser = new Map();
  const defaultsByUser = new Map();

  (Array.isArray(availabilityEntries) ? availabilityEntries : []).forEach((entry) => {
    if (!explicitByUser.has(entry.userId)) explicitByUser.set(entry.userId, []);
    explicitByUser.get(entry.userId).push(entry);
  });

  (Array.isArray(availabilityDefaults) ? availabilityDefaults : []).forEach((entry) => {
    if (!defaultsByUser.has(entry.userId)) defaultsByUser.set(entry.userId, []);
    defaultsByUser.get(entry.userId).push(entry);
  });

  return (Array.isArray(employees) ? employees : []).reduce((accumulator, employee) => {
    const userId = String(employee?.id || "");
    if (!userId) return accumulator;

    const mode = getUserAvailabilityMode(userId, userAvailabilityModes, companyAvailabilityMode);
    const relevantType = getRelevantRecordType(mode);

    (Array.isArray(days) ? days : []).forEach((day) => {
      const defaultRecords = (defaultsByUser.get(userId) || [])
        .map((record) => normalizeDefaultRecord(record, day))
        .filter(Boolean);
      const explicitRecords = (explicitByUser.get(userId) || [])
        .map((record) => normalizeExplicitRecord(record, day.key))
        .filter(Boolean);
      const records = selectEffectiveRecords(explicitRecords, defaultRecords, relevantType);
      const summary = summarizeRecords(records, mode);

      const key = `${userId}::${day.key}`;
      const current = accumulator[key];
      if (!current || statusPriority(summary.status) > statusPriority(current.status)) {
        accumulator[key] = summary;
      }
    });

    return accumulator;
  }, {});
}
